import moment from 'moment/moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModalContainer from '@components/modals/ModalContainer';
import TechPackMaterialSection from '@components/TechPackMaterialSection';
import TechPackMeasurementSection from '@components/TechPackMeasurementSection';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError } from '@components/Toast';

import CostingForm from '@features/costing/components/CostingForm';
import DesignSelectionModal from '@features/design/components/DesignSelectionModal';
import DesignUpdate from '@features/design/components/DesignUpdate';

import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { updateDesignViewModalInfo } from '@store/actions/DesignViewModalAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { useDesignViewModal } from '@store/reducers';

import { getSetInfo } from '@services/collectionService';
import {
	downloadTechPack,
	getGroupwiseDocByProductId,
	getProductArtBoardsByProductId,
	getProductInfoByProductId,
	getProductInfoByProductIds,
	getProductMaterialsByProductId
} from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { getFileType } from '@utils/Util';

import { MATERIALS_TYPE } from '@/constants/constant';

const TechPackView = () => {
	const {
		id: productId,
		isShowCostingButton = false,
		initialCostingId,
		isCostingRequestFromBuyer,
		isShowMergeTechPack,
		onCloseViewModal,
		onUpdateCostingStatus,
		isDefaultOpenBasicInfo = true,
		techPackDefaultIsDesignEdit = false,
		isShowCostingPanel,
		costingBrandInfo
	} = useDesignViewModal();

	const [isShowUpdateBasicInfo, setIsShowUpdateBasicInfo] = useState(isDefaultOpenBasicInfo);
	const [isShowNitexTechpack, setIsShowNitexTechpack] = useState(true);
	const [isPromiseResolved, setIsPromiseResolved] = useState(false);
	const [openCostingPanel, setOpenCostingPanel] = useState(false);
	const [downloadLoading, setDownloadLoading] = useState(false);
	const [documentActiveId, setDocumentActiveId] = useState(null);
	const [productArtBoards, setProductArtBoards] = useState({});
	const [productMaterials, setProductMaterials] = useState({});
	const [mergeProductList, setMergeProductList] = useState([]);
	const [selectedDocUrl, setSelectedDocUrl] = useState('');
	const [productWiseDoc, setProductWiseDoc] = useState({});
	const [mergeProducts, setMergeProducts] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [defaultIsDesignEdit] = useState(techPackDefaultIsDesignEdit);
	const [products, setProducts] = useState({});
	const [activeProductId, setActiveProductId] = useState(null);
	const [, setBaseprice] = useState(null);
	const closeButtonRef = useRef();
	const dispatch = useDispatch();

	useEffect(() => {
		(async () => {
			try {
				let params = `${productId}?checkBuyerSelectionStatus=true`;
				dispatch(fetchProductInfo(params));
				if (isShowMergeTechPack) {
					const setInfos = await getSetInfo(productId);
					setActiveProductId(setInfos?.setMemberProductIds[0]);
					setMergeProductList(setInfos.setMemberProductIds);
				}
			} catch {
				/* Empty Block */
			}
		})();
	}, [productId]);

	useEffect(() => {
		(async () => {
			dispatch(openLoader());
			try {
				const fetchPromises = mergeProductList.map(async (item) => {
					await fetchAllProductInfo(item);
				});
				await Promise.all(fetchPromises);
				setIsPromiseResolved(true);
				dispatch(closeLoader());
			} catch {
				setIsPromiseResolved(false);
				dispatch(closeLoader());
			}
		})();
	}, [mergeProductList]);

	useEffect(() => {
		if (isShowMergeTechPack && mergeProductList?.length > 0) {
			(async () => {
				const data = await getProductInfoByProductIds(`?ids=${mergeProductList.join(',')}`);
				setMergeProducts(data);
			})();
		}
	}, [mergeProductList]);

	useEffect(() => {
		return () => {
			if (closeButtonRef && closeButtonRef.current) closeButtonRef.current.click();
		};
	}, []);

	const processProductMaterials = (productMaterialsList) => {
		if (productMaterialsList && productMaterialsList.length > 0) {
			let tempFabricList = [];
			let tempTrimsList = [];
			let tempBrandingList = [];
			productMaterialsList.map((obj, index) => {
				obj.productMaterialIndexNumber = index;
				if ([MATERIALS_TYPE.MAIN_FABRIC, MATERIALS_TYPE.EXTRA_FABRIC].includes(obj.materialType)) {
					tempFabricList.push(obj);
				} else if ([MATERIALS_TYPE.TRIMS].includes(obj.materialType)) {
					tempTrimsList.push(obj);
				} else if ([MATERIALS_TYPE.BRANDING].includes(obj.materialType)) {
					tempBrandingList.push(obj);
				}
			});
			return {
				fabric: tempFabricList,
				trims: tempTrimsList,
				branding: tempBrandingList
			};
		}
	};

	const fetchAllProductInfo = async (productId) => {
		try {
			const docInfo = await getGroupwiseDocByProductId(productId);
			setProductWiseDoc((prev) => ({ ...prev, [productId]: docInfo }));
			setOpenCostingPanel(isShowCostingPanel);
			const productInfo = await getProductInfoByProductId(productId);
			setProducts((prev) => ({ ...prev, [productId]: productInfo }));

			const artBoards = await getProductArtBoardsByProductId(productId);
			setProductArtBoards((prev) => ({ ...prev, [productId]: artBoards }));

			const materials = await getProductMaterialsByProductId(productId);
			setProductMaterials((prev) => ({ ...prev, [productId]: processProductMaterials(materials) }));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		}
	};

	const getBasePrice = async () => {
		await ApiClient.GET('fetchInitialCostingFromCosting', initialCostingId)
			.then((response) => {
				setBaseprice(response?.data?.totalCost?.toFixed(2) || null);
			})
			.catch((error) => {
				toastError(error.response.data.message);
			});
	};

	const renderFabricList = (product) => {
		if (product?.fabrics?.length > 1) {
			return product?.fabrics?.map((item, index) => {
				index++;
				if (index < 3) {
					return (
						<React.Fragment key={`fabric_info_${index}`}>
							<p key={`fabric_${index}`} className="regular-14 gray_dark_01">
								{`Fabric ${index}`}: {item.fabricDetailsForPresentation}
							</p>
							<p key={`gsm_${index}`} className="regular-14 gray_dark_01">
								Weight: {item.weight}
							</p>
							<br />
						</React.Fragment>
					);
				}
			});
		} else if (product?.fabrics) {
			return (
				<>
					<p className="regular-14 gray_dark_01">
						Fabric: {product?.fabrics[0]?.fabricDetailsForPresentation}
					</p>
					<p className="regular-14 gray_dark_01">Weight: {product?.fabrics[0]?.weight}</p>
				</>
			);
		}
	};

	const renderPresentationViews = () => {
		return Object.keys(productWiseDoc || {})?.map((item) => {
			let productDocs = productWiseDoc[item];
			let productInfo = products[item];
			return productDocs?.productPresentationResponseList?.map((presentation) => {
				const fontImage = presentation?.documentResponse?.find(
					(document) => document.docType === 'FRONT_IMAGE'
				);
				const backImage = presentation?.documentResponse?.find(
					(document) => document.docType === 'BACK_IMAGE'
				);
				const fabricImage = presentation?.documentResponse?.find(
					(document) => document.docType === 'FABRIC_IMAGE'
				);
				const embellishment = presentation?.documentResponse?.find(
					(document) => document.docType === 'EMBELLISHMENT'
				);
				return (
					<div className="single-design-view-page" key={`single_design_${presentation.id}`}>
						<div className="single-artboard-title">
							<p className="regular-16 weight-500 gray_dark_01 m-0">{productInfo?.name}</p>
						</div>
						<div className="presentation-view-container">
							<div className="images-view-section">
								<div className="one-third full-photo">
									{fontImage?.docUrl && <img src={fontImage?.docUrl} alt="front" />}
								</div>
								<div className="one-third full-photo">
									{backImage?.docUrl && <img src={backImage?.docUrl} alt="front" />}
								</div>
								<div className="one-third fabric-photo">
									<div className="single-fabric">
										{fabricImage?.docUrl && <img src={fabricImage?.docUrl} alt="front" />}
									</div>
									<div className="single-fabric">
										{embellishment?.docUrl && (
											<img src={embellishment?.docUrl} alt="front" />
										)}
									</div>
									<p className="regular-14 gray_dark_01">Item: {productInfo?.name}</p>
									{renderFabricList(productInfo)}
								</div>
							</div>
						</div>
					</div>
				);
			});
		});
	};

	const renderKeyDetails = () => {
		return Object.keys(productWiseDoc)?.map((item) => {
			let productDocs = productWiseDoc[item];
			let productInfo = products[item];
			return productDocs?.productPresentationResponseList
				?.filter((productPresentation) => productPresentation.isDefault)
				?.map((presentation) => {
					const embellishment = presentation?.documentResponse?.find(
						(document) => document.docType === 'EMBELLISHMENT'
					);
					// If there's no embellishment, skip rendering for this presentation
					if (!embellishment) return null;

					// Use a safe key and only render when docUrl is available
					if (!embellishment?.docUrl) return null;

					return (
						<div
							className="single-design-view-page"
							key={`product_embellishment_${embellishment.id || presentation.id}`}
						>
							<div className="single-artboard-title">
								<p className="regular-16 weight-500 gray_dark_01 m-0">
									Key details ({productInfo?.name})
								</p>
							</div>
							<div className="full-artboard">
								<img src={embellishment.docUrl} alt="" />
							</div>
						</div>
					);
				});
		});
	};

	const renderArtBoards = () => {
		return Object.keys(productArtBoards)?.map((item) => {
			let artBoards = productArtBoards[item];
			let productInfo = products[item];
			return artBoards?.map((artBoard) => {
				return (
					<div className="single-design-view-page" key={`artBoard_${artBoard.id}`}>
						<div className="single-artboard-title">
							<p className="regular-16 weight-500 gray_dark_01 m-0">
								{artBoard.name} ({productInfo?.name})
							</p>
						</div>
						<div className="full-artboard">
							<img src={artBoard.docUrl} alt="" />
						</div>
					</div>
				);
			});
		});
	};

	const renderHowToMeasureDocuments = () => {
		return Object.keys(productWiseDoc)?.map((item) => {
			let productDocs = productWiseDoc[item];
			let productInfo = products[item];
			return (
				productDocs?.howToMeasureDocument?.docUrl && (
					<div className="single-design-view-page">
						<div className="single-artboard-title">
							<p className="regular-16 weight-500 gray_dark_01 m-0">
								HOW TO MEASURE ({productInfo?.name})
							</p>
						</div>
						<div className="full-artboard">
							<img src={productDocs?.howToMeasureDocument?.docUrl} alt="" />
						</div>
					</div>
				)
			);
		});
	};

	const renderMeasurementTables = () => {
		return mergeProductList?.map((item, index) => {
			return (
				<React.Fragment key={`measurement_table_${index}`}>
					<TechPackMeasurementSection productId={item} productInfo={products[item]} />
				</React.Fragment>
			);
		});
	};

	const onCloseALl = () => {
		setEditModal(false);
		setDocumentActiveId(null);
		setIsShowNitexTechpack(false);
		onCloseViewModal();
	};

	const renderCloseButton = () => {
		return (
			<button type="button" className="close" onClick={() => onCloseALl()} ref={closeButtonRef}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="25"
					viewBox="0 0 24 25"
					fill="none"
				>
					<path
						d="M18 6.21423L6 18.6428"
						stroke="white"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<path
						d="M6 6.21423L18 18.6428"
						stroke="white"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
		);
	};

	const handleOpenCostingPannel = () => {
		setOpenCostingPanel((prev) => !prev);
	};

	const downloadTechpack = async () => {
		let currentTime = moment(new Date()).format('DD MMM [AT] hh:mm A');
		setDownloadLoading(true);
		try {
			const data = await downloadTechPack(productId, currentTime);
			setDownloadLoading(false);
			window.open(data, '_parent');
		} catch {
			setDownloadLoading(false);
		}
	};

	const renderTopBar = () => {
		return (
			<div className="modal-header">
				<nav className="navbar navbar-expand-lg navbar-light design-tool-header">
					<>
						<div className="toggle-menu-btn">
							<a
								className="back-btn"
								onClick={() => {
									onCloseViewModal();
								}}
							>
								<img src={`${process.env.PUBLIC_URL}/icons/Left-arrow.svg`} alt="back" />
							</a>
						</div>

						<div className="design-selection-dropdown one-third-item">
							<div className="d-flex justify-content-center align-items-center">
								<span className="cursor-pointer" onClick={() => setEditModal(!editModal)}>
									<img
										src={`${process.env.PUBLIC_URL}/icons/narrow-view-dark.svg`}
										alt="more"
										className="more-icon"
									/>
								</span>
								<div
									className="dropdown-btn d-flex align-items-center cursor-pointer"
									onClick={() => setIsShowUpdateBasicInfo((prev) => !prev)}
								>
									<span className="regular-14 gray_dark_01">
										/{products[Object.keys(products)[0]]?.setProductInfo?.referenceNumber}
									</span>
									<img
										src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
										alt=""
									/>
								</div>
							</div>
						</div>
						<div className="profile-and-comments one-third-item" />
					</>
					<div>
						<ul className="d-flex menus">
							{isShowCostingButton && (
								<li onClick={handleOpenCostingPannel} className="cursor-pointer mr-2">
									{!openCostingPanel ? (
										<img
											src={`${process.env.PUBLIC_URL}/icons/Left-arrow.svg`}
											alt="message"
										/>
									) : (
										<img
											className="pr-1"
											src={`${process.env.PUBLIC_URL}/icons/close.svg`}
											alt="message"
										/>
									)}
									{!openCostingPanel ? 'Costing' : 'Close'}
								</li>
							)}

							<li
								className="cursor-pointer"
								onClick={() => {
									downloadTechpack();
								}}
							>
								<img
									src={`${process.env.PUBLIC_URL}/icons/download-gray-dark-02.svg`}
									alt="download"
								/>
							</li>
						</ul>
					</div>
				</nav>

				{renderCloseButton()}
			</div>
		);
	};

	const updateDesignSelection = (product) => {
		dispatch(
			updateDesignViewModalInfo({
				mergeProductList: product.isSet ? product.setMemberProductIds : [],
				isShowMergeTechPack: product.isSet,
				id: product.id
			})
		);
	};

	const renderDocuments = () => {
		const fileType = getFileType(selectedDocUrl);
		if (fileType === 'PDF') {
			return (
				<div className="embed-responsive embed-responsive-16by9">
					<iframe className="embed-responsive-item" src={selectedDocUrl} allowFullScreen />
				</div>
			);
		} else
			return (
				<div className="no-preview-file">
					<div>
						<p className="semibold-14 gray_dark_02">No preview available </p>
						<button
							className="button outline size32 mt-3"
							onClick={() => window.open(selectedDocUrl, '_blank')}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/download-dark-20.svg`}
								alt="download"
								className="mr-1"
							/>
							Download file
						</button>
					</div>
				</div>
			);
	};

	const getAllNotImageDocumentsFromProduct = () => {
		let documentList = [];
		for (let key in products) {
			if (products[key]?.allNotImageDocuments && Array.isArray(products[key]?.allNotImageDocuments)) {
				let documents = products[key]?.allNotImageDocuments?.map((docs) => ({
					...docs,
					productId: products[key]
				}));
				documentList = [...documentList, ...(documents || [])];
			}
		}
		return documentList;
	};

	const renderMergeProducts = () => {
		return mergeProducts?.map((item) => {
			return (
				<img
					src={item?.designDocuments?.[0]?.docUrl}
					alt="style"
					className={activeProductId === item.id ? 'active' : ''}
					key={`product_${item.id}`}
					onClick={() => setActiveProductId(item.id)}
				/>
			);
		});
	};
	return (
		<ModalContainer onCloseModal={onCloseViewModal} className="design-view-modal">
			{renderTopBar()}
			{editModal && (
				<DesignSelectionModal
					closeModal={setEditModal}
					setProduct={updateDesignSelection}
					productId={productId}
				/>
			)}
			{activeProductId && isShowUpdateBasicInfo && (
				<div className="sample-style-detail ">
					{mergeProducts?.length > 0 && (
						<div className="set-all-styles-images d-flex flex-wrap mb-3">
							{renderMergeProducts()}
						</div>
					)}
					<DesignUpdate
						productId={activeProductId}
						requestFrom="SET_PRODUCT_VIEW"
						onSetNitexTab={() => {
							setIsShowNitexTechpack(true);

							setDocumentActiveId(null);
						}}
						activeId={documentActiveId}
						isNitexTechpack={isShowNitexTechpack}
						onChangeActiveTab={(value) => {
							setIsShowNitexTechpack(false);

							setDocumentActiveId(value?.id);

							setSelectedDocUrl(value?.docUrl);
						}}
						setProductDocumentList={getAllNotImageDocumentsFromProduct()}
						defaultIsDesignEdit={defaultIsDesignEdit}
					/>
				</div>
			)}

			{downloadLoading && <TimeBasedLoader startLoader={downloadLoading} />}
			<div className="modal-dialog" role="document">
				<div className="modal-content">
					<div className="modal-body">
						{isShowNitexTechpack && !documentActiveId && (
							<>
								{renderPresentationViews()}
								{renderKeyDetails()}
								{renderArtBoards()}
								<TechPackMaterialSection
									productMaterials={productMaterials}
									products={products}
								/>
								{renderHowToMeasureDocuments()}
								{isPromiseResolved && renderMeasurementTables()}
							</>
						)}
						{!isShowNitexTechpack && documentActiveId && renderDocuments()}
					</div>
				</div>
			</div>
			{openCostingPanel && (
				<CostingForm
					isCostingForSetItem={true}
					costingId={isCostingRequestFromBuyer ? initialCostingId : productId}
					onUpdateCostingStatus={() => {
						if (onUpdateCostingStatus) {
							onUpdateCostingStatus(productId);
						}
						if (isCostingRequestFromBuyer) {
							getBasePrice();
						}
					}}
					productId={productId}
					isCostingRequestFromBuyer={!!isCostingRequestFromBuyer}
					costingBrandInfo={costingBrandInfo}
				/>
			)}
		</ModalContainer>
	);
};

export default TechPackView;
